import * as React from 'react';
import { getLocalizedData } from '@braindate/domain/lib/base/l10n/util';

import classNames from 'classnames';
import { compose } from 'ramda';
import { useSelector } from 'react-redux';
import createUseThemeStyles from 'src/shared/app/base/util/createUseThemeStyles';
import useRoutes from 'src/shared/app/base/route/hook/useRoutes';
import { useIntl } from 'react-intl';
import useEventBraindateName from 'src/shared/domain/event/hook/useEventBraindateName';
import useFeatureDecisions from 'src/shared/app/feature/hook/useFeatureDecisions';

import styles from './MainNav.style';
import messages from 'src/shared/app/nav/l10n/navigationL10n';

import { getCustomMenuLinks } from 'plugin/custom-menu/util/customMenuUtils';
import { getNotificationCount } from 'src/shared/app/notification/selector/appNotificationSelectors';
import useEvent from 'src/shared/domain/event/hook/useEvent';

import {
  homeRoute,
  newTopicRoute,
  braindatesRoute,
  peopleFeedRoute,
} from 'src/shared/app/base/route/setting/routeSettings';

import MainNavLink from './MainNavLink';
import MainNavSubMenu from './MainNavSubMenu';

import LazyBraindatesMain from 'src/shared/app/braindate/component/main/LazyBraindatesMain';
import LazyMarketMain from 'src/shared/app/market/component/MarketMain';
import LazyNewTopicMain from 'src/shared/app/topic/new/component/LazyNewTopicMain';
import NotificationsMenu from 'src/shared/app/nav/component/notifications-menu/NotificationsMenu';

import BellIcon from 'src/shared/ui/component/icon/BellIcon';
import Plus2Icon from 'src/shared/ui/component/icon/Plus2Icon';
import CalendarIcon2 from 'src/shared/ui/component/icon/CalendarIcon2';
import MarketNavItemIcon from 'src/shared/ui/component/icon/MarketNavItemIcon';
import NotificationBadge from 'src/shared/ui/component/messaging/NotificationBadge';

const useStyles = createUseThemeStyles(styles);

type Props = {
  className?: string,
  wrapToContent?: boolean,
  isMobile?: boolean,
};

const MainNav = ({ className, wrapToContent, isMobile }: Props) => {
  const classes = useStyles({ wrapToContent });
  const intl = useIntl();

  const { braindatePlural } = useEventBraindateName();
  const { isCustomMenuEnabled } = useFeatureDecisions();

  const event = useEvent();
  const notificationsCount = useSelector(getNotificationCount);
  const hasNotifications = notificationsCount > 0;

  const externalItems =
    (isCustomMenuEnabled && getCustomMenuLinks(event)) || [];

  const [notificationsMenuOpened, setNotificationsMenuOpened] =
    React.useState(false);

  const [homePath, newTopicPath, braindatesPath, peopleFeedPath] = useRoutes(
    homeRoute,
    newTopicRoute,
    braindatesRoute,
    peopleFeedRoute,
  );

  /*
  |----------------------------------------------------------------------------
  | Hooks
  |----------------------------------------------------------------------------
  */

  /*
   |----------------------------------------------------------------------------
   | Elements
   |----------------------------------------------------------------------------
   */

  const getExternalLinkLabel = (link: Object) =>
    getLocalizedData(link, intl.locale).label;
  const getExternalLinkHref = (link: Object) => link.href;
  const getExternalLinkIcon = (link: Object) => link.icon;

  const externalItemsElts = isCustomMenuEnabled
    ? externalItems.map((link, key) => (
        <MainNavLink
          title={getExternalLinkLabel(link)}
          to={getExternalLinkHref(link)}
          target="_blank"
          rel="noreferrer"
          className={classes.submenuItem}
          key={`link-${key}`}
        >
          <img
            className="external-link-image"
            src={getExternalLinkIcon(link)}
            alt={intl.formatMessage(messages.visitLink)}
          />
        </MainNavLink>
      ))
    : [];

  const badgeElt = hasNotifications && (
    <NotificationBadge
      layoutClass={classNames(classes.badge, {
        [classes.badgeMenuOpened]: notificationsMenuOpened,
      })}
    />
  );

  return (
    <nav
      className={classNames(classes.root, className)}
      aria-label={intl.formatMessage(
        isMobile ? messages.mobileLabel : messages.label,
      )}
    >
      <ul className={classes.list}>
        {isCustomMenuEnabled &&
          externalItemsElts.map((item, key) => (
            <li
              className={classNames([classes.item, classes.itemDesktopOnly])}
              key={key}
            >
              {item}
            </li>
          ))}

        <li className={classNames([classes.item, classes.itemMobileOnly])}>
          <MainNavLink
            title={intl.formatMessage(messages.newTopic)}
            to={newTopicPath}
            icon={<Plus2Icon />}
            match={[newTopicPath]}
            preloadComp={LazyNewTopicMain}
            exact
          />
        </li>

        <li className={classes.item}>
          <MainNavLink
            title={intl.formatMessage(messages.market)}
            to={homePath}
            className={classes.itemHome}
            icon={<MarketNavItemIcon />}
            match={[peopleFeedPath]}
            preloadComp={LazyMarketMain}
            exact
          />
        </li>

        <li className={classes.item}>
          <MainNavLink
            title={intl.formatMessage(messages.myBraindates, {
              braindatePlural,
            })}
            className={classes.itemBraindates}
            to={braindatesPath}
            icon={<CalendarIcon2 />}
            preloadComp={LazyBraindatesMain}
          />
        </li>

        <li className={classNames(classes.item, classes.itemDesktopOnly)}>
          <NotificationsMenu
            isOpened={notificationsMenuOpened}
            setIsOpened={setNotificationsMenuOpened}
          >
            <MainNavLink
              title={intl.formatMessage(messages.notifications)}
              icon={<BellIcon />}
              active={notificationsMenuOpened}
              className={classNames(
                classes.submenuItem,
                classes.itemNotifications,
              )}
            >
              {badgeElt}
            </MainNavLink>
          </NotificationsMenu>
        </li>

        {isCustomMenuEnabled && (
          <MainNavSubMenu
            className={classNames(classes.item, classes.itemMobileOnly)}
            items={externalItemsElts}
          />
        )}
      </ul>
    </nav>
  );
};

export default compose(React.memo)(MainNav);
